import { useRef, useState } from "react";

import {
  DndContext,
  closestCenter
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  rectSortingStrategy
} from "@dnd-kit/sortable";

import {
  useSortable
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

function SortableItem({ photo, index, removePhoto, setCover }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
    >

      {/* IMAGE */}
      <div className="cursor-grab">
        <img
          src={photo.url}
          alt=""
          className={`w-full h-32 object-cover rounded-xl 
            ${index === 0 ? "ring-2 ring-green-500" : ""}
          `}
        />
    </div>

    <div
        {...attributes}
        {...listeners}
        className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 text-xs rounded cursor-grab"
        >
        Drag
    </div>

      {/* COVER BADGE */}
      {index === 0 && (
        <span className="absolute bottom-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded">
          Couverture
        </span>
      )}

      {/* ACTIONS */}
      <div className="absolute top-2 right-2 flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition">

        {/* SET COVER */}
        <button
            onClick={(e) => {
                e.stopPropagation();
                setCover(index);
                }}
            className="bg-white/90 backdrop-blur-sm text-xs px-2 py-1 rounded-full shadow hover:scale-105 transition"
        >
            Cover
        </button>

        {/* DELETE PREMIUM */}
        <button
            onClick={(e) => {
                e.stopPropagation();
                removePhoto(index);
            }}
            className="w-8 h-8 flex items-center justify-center 
                        rounded-full bg-black text-white
                        shadow-lg
                        hover:bg-red-500 
                        hover:scale-110 active:scale-95 transition"
            >
            ✕
        </button>

        </div>

    </div>
  );
}

export default function PhotosStep({ formData, setFormData }) {

  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  // CLOUDINARY
  const uploadToCloudinary = async (file) => {
    const formDataCloud = new FormData();
    formDataCloud.append("file", file);
    formDataCloud.append("upload_preset", "komorotel_unsigned");

    const res = await fetch(
      "https://api.cloudinary.com/v1_1/dhwqnwnvh/image/upload",
      {
        method: "POST",
        body: formDataCloud,
      }
    );

    const result = await res.json();
    return result.secure_url;
  };

  // UPLOAD
  const handleFiles = async (files) => {
    const filesArray = Array.from(files);

    setUploading(true);

    try {
        const generateId = () =>
            typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : Math.random().toString(36).substring(2, 9);
        const uploadedUrls = await Promise.all(
        filesArray.map(file => uploadToCloudinary(file))
        );

        const newPhotos = uploadedUrls.map(url => ({
        id: generateId(),
        url
        }));

        setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos]
        }));

    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
  };

  // DELETE
  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  // ⭐ SET COVER
  const setCover = (index) => {
    const newPhotos = [...formData.photos];
    const [selected] = newPhotos.splice(index, 1);
    newPhotos.unshift(selected);

    setFormData(prev => ({
        ...prev,
        photos: newPhotos
    }));
    };

  // 🔥 DRAG END
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over) return;

        if (active.id !== over.id) {
            const oldIndex = formData.photos.findIndex(p => p.id === active.id);
            const newIndex = formData.photos.findIndex(p => p.id === over.id);

            const newOrder = arrayMove(formData.photos, oldIndex, newIndex);

            setFormData(prev => ({
            ...prev,
            photos: newOrder
            }));
        }
        };

  return (
    <div className="space-y-8">

      {/* TITLE */}
      <div>
        <h2 className="text-2xl font-semibold">Photos</h2>
        <p className="text-gray-500 mt-1">
          Ajoutez, organisez et choisissez une photo principale
        </p>
      </div>

      {/* DROPZONE */}
      <div
        onClick={() => inputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center cursor-pointer hover:border-green-500 transition"
      >
        {uploading ? (
          <p className="text-green-600 font-medium">
            Upload en cours...
          </p>
        ) : (
          <>
            <p className="text-gray-500">
              Glissez vos images ou cliquez
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Première image = couverture
            </p>
          </>
        )}

        <input
          type="file"
          multiple
          accept="image/*"
          ref={inputRef}
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* GRID + DRAG */}
      {formData.photos.length > 0 && (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={formData.photos.map(p => p.id)}
            strategy={rectSortingStrategy}
            >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

              {formData.photos.map((photo, index) => (
                <SortableItem
                    key={photo.id}
                    photo={photo}
                    index={index}
                    removePhoto={removePhoto}
                    setCover={setCover}
                />
                ))}

            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* COUNT */}
      {formData.photos.length > 0 && (
        <p className="text-sm text-gray-500">
          {formData.photos.length} photo(s)
        </p>
      )}

    </div>
  );
}